import type {
  ExternalWorkIntegrationStatusValues,
  JiraCustomFieldFilter,
  WorkIntegration_Keys,
} from '@/lib/api/types/work-integrations';

export const workIntegrationsQueryKeys = {
  workIntegrations: ['workIntegrations'],
  workIntegrationsAll: (apiOptions?: {
    limit?: number;
    filter?: {
      statuses?: ExternalWorkIntegrationStatusValues[];
      ids?: number[];
      loopIds?: number[];
    };
  }) => ['workIntegrations', apiOptions],
  jiraInstances: () => ['workIntegrations', 'instances'],
  jiraInstanceIssueTypes: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraInstanceIssueTypes',
    { instanceId },
  ],
  jiraItemLevelMappings: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraItemLevelMappings',
    { instanceId },
  ],
  jiraItemTypeMappings: ({ instanceId }: { instanceId?: string }) => [
    'workIntegrations',
    'jiraItemTypeMappings',
    { instanceId },
  ],
  jiraFields: ({
    oauthCredentialsId,
    instanceId,
    projectId,
  }: {
    oauthCredentialsId?: number;
    instanceId?: string;
    projectId?: string;
  }) => ['workIntegrations', 'jiraFields', { oauthCredentialsId, instanceId, projectId }],
  jiraCustomFields: ({
    credentialsId,
    instanceId,
    projectId,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectId?: string;
  }) => ['workIntegrations', 'jiraCustomFields', { credentialsId, instanceId, projectId }],
  jiraFieldOptions: ({
    credentialsId,
    instanceId,
    fieldId,
    fieldValue,
  }: {
    credentialsId?: number;
    instanceId?: string;
    fieldId?: string;
    fieldValue?: string;
  }) => [
    'workIntegrations',
    'jiraFieldOptions',
    { credentialsId, instanceId, fieldId, fieldValue },
  ],
  jiraUserSuggestions: ({
    credentialsId,
    instanceId,
    searchTerm,
  }: {
    credentialsId?: number;
    instanceId?: string;
    searchTerm?: string;
  }) => ['workIntegrations', 'jiraUserSuggestions', { credentialsId, instanceId, searchTerm }],
  jiraUserBulk: ({
    credentialsId,
    instanceId,
    accountIds,
  }: {
    credentialsId?: number;
    instanceId?: string;
    accountIds?: string[];
  }) => [
    'workIntegrations',
    'jiraUserBulk',
    { credentialsId, instanceId, accountIds: accountIds ? [...accountIds].sort() : undefined },
  ],
  jiraProjectVersions: ({
    credentialsId,
    instanceId,
    projectKey,
    includeArchived,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectKey?: string;
    includeArchived?: boolean;
  }) => [
    'workIntegrations',
    'jiraProjectVersions',
    { credentialsId, instanceId, projectKey, includeArchived },
  ],
  jiraProjectComponents: ({
    credentialsId,
    instanceId,
    projectKey,
  }: {
    credentialsId?: number;
    instanceId?: string;
    projectKey?: string;
  }) => ['workIntegrations', 'jiraProjectComponents', { credentialsId, instanceId, projectKey }],
  jiraProjects: ({ cloudId }: { cloudId?: string }) => [
    'workIntegrations',
    'jiraProjects',
    { cloudId },
  ],
  jiraIssueTypesAndStatuses: ({
    oauthCredentialsId,
    cloudId,
    projectIdOrKey,
  }: {
    oauthCredentialsId?: number;
    cloudId?: string;
    projectIdOrKey?: string;
  }) => [
    'workIntegrations',
    'jiraIssueTypesAndStatuses',
    { oauthCredentialsId, cloudId, projectIdOrKey },
  ],
  jiraIssueTypesWithHierarchy: ({
    oauthCredentialsId,
    cloudId,
    projectIdOrKey,
  }: {
    oauthCredentialsId?: number;
    cloudId?: string;
    projectIdOrKey?: string;
  }) => [
    'workIntegrations',
    'jiraIssueTypesWithHierarchy',
    { oauthCredentialsId, cloudId, projectIdOrKey },
  ],
  jiraWorkItemCount: ({
    cloudId,
    projectId,
    typeIds = [],
    statusIds = [],
    includeWorkAfter,
    jql,
    customFieldFilters = [],
  }: {
    cloudId?: string;
    projectId?: string;
    typeIds?: string[];
    statusIds?: string[];
    includeWorkAfter?: string;
    jql?: string;
    customFieldFilters?: JiraCustomFieldFilter[];
  }) => [
    'jiraWorkItemCount',
    {
      cloudId,
      projectId,
      typeIds,
      statusIds,
      includeWorkAfter,
      jql,
      customFieldFilters,
    },
  ],
  jiraJqlValidation: ({
    oauthCredentialsId,
    cloudId,
    jql,
  }: {
    oauthCredentialsId?: number;
    cloudId?: string;
    jql?: string;
  }) => ['workIntegrations', 'jiraJqlValidation', { oauthCredentialsId, cloudId, jql }],
  workItemPreview: ({ projectId }: { projectId?: string }) => [
    'workIntegrations',
    'workItemPreview',
    { projectId },
  ],
  workIntegrationErrors: ({ workIntegrationId }: { workIntegrationId?: number }) => [
    'workIntegrations',
    'errors',
    { workIntegrationId },
  ],
  pendingItemsCount: ({ integrationId }: { integrationId?: number }) => [
    'workIntegrations',
    'pendingItemsCount',
    { integrationId },
  ],
  syncFailedItemsAll: () => ['workIntegrations', 'syncFailedItems'],
  syncFailedItemsForIntegration: ({
    integrationId,
    page,
    pageSize,
  }: {
    integrationId?: number;
    page?: number;
    pageSize?: number;
  }) => ['workIntegrations', 'syncFailedItems', 'integration', { integrationId, page, pageSize }],
  syncFailedItemsForItem: ({
    itemIdOrSourceItemKey,
    page,
    pageSize,
  }: {
    itemIdOrSourceItemKey?: string;
    page?: number;
    pageSize?: number;
  }) => ['workIntegrations', 'syncFailedItems', 'item', { itemIdOrSourceItemKey, page, pageSize }],
  workIntegrationStatus: () => ['workIntegrations', 'status'],
  workIntegrationItemsPrefix: () => ['workIntegrations', 'items'] as const,
  workIntegrationItems: ({ itemIds }: { itemIds?: Set<number> }) => [
    'workIntegrations',
    'items',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  itemSyncStatusesPrefix: () => ['workIntegrations', 'itemSyncStatuses'] as const,
  itemSyncStatuses: ({ itemIds }: { itemIds?: Set<number> }) => [
    'workIntegrations',
    'itemSyncStatuses',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  schedules: ({ itemIds }: { itemIds?: Set<number> }) => [
    'schedules',
    'items',
    { itemIds: itemIds ? Array.from(itemIds).sort() : undefined },
  ],
  scheduleByItemId: (itemId: number) => ['schedules', 'single', itemId] as const,
  integrationSchedule: ({ integrationId }: { integrationId: number | undefined }) =>
    ['workIntegrations', 'schedule', { integrationId }] as const,
  estimations: {
    byIds: (estimationIds: Set<number>) => [
      'estimations',
      'byIds',
      { estimationIds: estimationIds.size > 0 ? Array.from(estimationIds).sort() : undefined },
    ],
  },
  integrationInstances: (sourceSystemType?: WorkIntegration_Keys) => [
    'workIntegrations',
    'registered-instances',
    { sourceSystemType },
  ],
  instanceFieldMappings: ({
    numericInstanceId,
    projectId,
  }: {
    numericInstanceId?: number;
    projectId?: string;
  }) => ['workIntegrations', 'instanceFieldMappings', { numericInstanceId, projectId }],
  instanceFieldMappingUsage: ({ numericInstanceId }: { numericInstanceId?: number }) => [
    'workIntegrations',
    'instanceFieldMappingUsage',
    { numericInstanceId },
  ],
  fieldValueMappings: ({ instanceFieldMappingId }: { instanceFieldMappingId?: number }) => [
    'workIntegrations',
    'fieldValueMappings',
    { instanceFieldMappingId },
  ],
  loopFieldValues: ({ instanceFieldMappingId }: { instanceFieldMappingId?: number }) => [
    'workIntegrations',
    'loopFieldValues',
    { instanceFieldMappingId },
  ],
  jiraFieldValues: ({
    instanceFieldMappingId,
    query,
  }: {
    instanceFieldMappingId?: number;
    query?: string;
  }) => ['workIntegrations', 'jiraFieldValues', { instanceFieldMappingId, query }],
  integrationCustomFieldMappings: ({ integrationId }: { integrationId?: number }) => [
    'workIntegrations',
    'integrationCustomFieldMappings',
    { integrationId },
  ],
  oauthCredentialStatuses: ({ credentialIds }: { credentialIds: number[] }) => [
    'workIntegrations',
    'oauthCredentialStatuses',
    { credentialIds },
  ],
};
